import { NavLinks } from '@/components/NavLinks';
import { chatApi } from '@/store/api/chat.api';
import { setCountMessages } from '@/store/reducers/chat.slice';
import { Drawer, Grid, Layout, Menu, Spin, theme } from 'antd';
import React, { useEffect, useState } from 'react';
// @ts-ignore
import AsteriTitle from 'src/assets/asteri-title.svg?react';

import { AUTH_DATA_STORAGE_KEY } from '../common/constants';
import { getRequiredDocSelect, getTemplate } from '../common/helpers';
import ChangePasswordModal from '../components/ChangePasswordModal';
import { useTypedDispatch } from '../hooks/useTypedDispatch';
import { DocTypeClass } from '../pages/Registration/Registration.interfaces';
import {
  Country,
  DocumentTemplate,
  Question,
  VirtualAsset,
} from '../store/api/types.api';
import { userApi } from '../store/api/user.api';
import { setAccessToken, setMobile } from '../store/reducers/common.slice';
import {
  setCountries,
  setCurrency,
  setDocuments,
  setRequiredDocSelect,
  setTemplates,
  setTests,
  setUserInfo,
  setVirtualAssets,
} from '../store/reducers/registration.slice';
import { Header } from './Header';
import {
  getCountriesSelect,
  getCurrencySelect,
  getVirtualActiveSelect,
} from './MainLayout.helpers';
import styles from './MainLayout.module.scss';
import { Sidebar } from './Sidebar/Sidebar';

const { Content } = Layout;
const { useBreakpoint } = Grid;

type Props = {
  children?: React.ReactNode;
};

export const MainLayout: React.FC<Props> = ({ children }) => {
  const screens = useBreakpoint();
  const isMobile = !screens.md; // < 768px

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const [isLoadingRequests, setIsLoadingRequests] = useState(true);
  const [drawerVisible, setDrawerVisible] = useState(false);

  const { data: userInfo } = userApi.useGetUserInfoQuery();
  const [getCatalog] = userApi.useGetCatalogMutation();
  const { data: countMessages } = chatApi.useGetCountMessagesQuery();

  const styleContent = {
    margin: '24px 16px',
    padding: isMobile ? 16 : 24,
    minHeight: 280,
    background: colorBgContainer,
    borderRadius: borderRadiusLG,
  };

  const [collapsed, setCollapsed] = useState(false);

  const dispatch = useTypedDispatch();

  const [logout] = userApi.useLazyLogoutQuery();

  const handleLogoutBtnClick = (): void => {
    logout().finally(() => {
      localStorage.removeItem(AUTH_DATA_STORAGE_KEY);
      dispatch(setAccessToken(null));
    });
  };

  const onCloseMobileMenu = () => {
    console.log('Mobile menu closed');
    setDrawerVisible(false);
  };

  useEffect(() => {
    Promise.all([
      getCatalog({ name: 'country' }),
      getCatalog({ name: 'doctype' }),
      getCatalog({ name: 'test' }),
      getCatalog({ name: 'virtualAsset' }),
      getCatalog({ name: 'currency' }),
    ])
      .then((res) => {
        // Добавляем select документов, которые нужны
        dispatch(setDocuments(res[1].data?.data as DocumentTemplate[]));

        // Добавляем селекты шаблонов, которые есть для необходимых документов
        const selectDocumentsTemplate = getTemplate(
          res[1].data?.data as DocumentTemplate[],
        );

        dispatch(setTemplates(selectDocumentsTemplate));

        // Добавляем select стран
        dispatch(
          setCountries(getCountriesSelect(res[0].data?.data as Country[])),
        );

        // Добавляем select для выбора необходимых документов
        // Берем только те, которые не blocked и не подписанные/не подписанные договора
        dispatch(
          setRequiredDocSelect(
            getRequiredDocSelect(
              (res[1].data?.data as DocumentTemplate[]).filter(
                (doc) =>
                  !(
                    doc.docTypeClass.includes(DocTypeClass.SIGNED_CONTRACT) ||
                    doc.docTypeClass.includes(DocTypeClass.NOT_SIGNED_CONTRACT)
                  ),
              ),
            ),
          ),
        );

        // Добавляем тесты
        dispatch(setTests(res[2].data?.data as Question[]));

        // Добавляем виртуальные активы
        dispatch(
          setVirtualAssets(
            getVirtualActiveSelect(res[3].data?.data as VirtualAsset[]),
          ),
        );

        // Добавляем select валют
        dispatch(setCurrency(getCurrencySelect(res[4].data?.data as any)));

        setIsLoadingRequests(false);
      })
      .catch(() => {
        console.error('catalogs loading error');
      });
  }, [dispatch, getCatalog]);

  useEffect(() => {
    if (userInfo) {
      dispatch(setUserInfo(userInfo));
    }
    dispatch(setMobile(isMobile));
  }, [dispatch, isMobile, userInfo]);

  useEffect(() => {
    if (
      countMessages &&
      typeof countMessages === 'object' &&
      'count' in countMessages
    ) {
      const count = (countMessages as { count: number }).count ?? 0;
      dispatch(setCountMessages(count));
    }
  }, [countMessages, dispatch]);

  return isLoadingRequests ? (
    <div className={styles.loader}>
      {/* <AsteriTitle /> */}
      {/* Пока не удалять */}
      <Spin size="large" />
    </div>
  ) : (
    <Layout className={styles.layout}>
      {!isMobile && (
        <Sidebar onCloseMobileMenu={onCloseMobileMenu} collapsed={collapsed} />
      )}

      <Layout>
        <Header
          setDrawerVisible={setDrawerVisible}
          name={userInfo?.name}
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          handleLogout={handleLogoutBtnClick}
        />

        <Content style={styleContent}>{children}</Content>
      </Layout>

      {userInfo && (
        <ChangePasswordModal
          isOpen={userInfo.passwordChangeRequired ?? false}
        />
      )}

      {/* Drawer для мобильного меню */}
      <Drawer
        placement="left"
        closable
        onClose={onCloseMobileMenu}
        open={drawerVisible}
      >
        <NavLinks onCloseMobileMenu={onCloseMobileMenu} isMobile />
      </Drawer>
    </Layout>
  );
};
